import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import type { FeedEventPayload, FeedItem } from '@knocklabs/client'
import {
  KnockFeedProvider,
  KnockProvider as KnockProviderBase,
  useKnockFeed,
} from '@knocklabs/react'

import '@knocklabs/react/dist/index.css'
import { useQueryClient } from '@tanstack/react-query'
import { useTheme } from 'next-themes'

import { useAuthInfo } from '@sweetauth/core'

import { chatSessionListQueryKeys } from '@/components/chat-v2/hooks/chatSessionQueryKeys'

import { KNOCK_IN_APP_CHANNEL_ID, KNOCK_PUBLIC_KEY } from '@/constants/env'

import { useNotificationToken } from '@/hooks/notifications/useNotificationToken'

import {
  DEFAULT_AUTH_CONSISTENCY_BOOTSTRAP_WINDOW_MS,
  extendAuthConsistencyWindow,
} from '@/lib/fetch/spiceConsistencyBootstrap'

import type { NotificationProviderProps } from '@/notifications/types'

const SHARED_THREAD_WORKFLOW_KEY = 'shared-thread'

/**
 * Context to track whether the Knock provider is ready (authenticated).
 * This allows child components to know if they can use Knock hooks.
 */
interface KnockContextValue {
  isReady: boolean
}

const KnockContext = createContext<KnockContextValue>({ isReady: false })

/**
 * Hook to check if the Knock provider is ready.
 * Use this in components that need to conditionally render Knock-dependent UI.
 */
export const useKnockReady = () => {
  const context = useContext(KnockContext)
  return context.isReady
}

/**
 * Re-export useKnockFeed for convenience so components don't need to import from @knocklabs/react directly.
 */
export { useKnockFeed }

const isSharedThreadNotification = (item: FeedItem) => {
  return item.source.key === SHARED_THREAD_WORKFLOW_KEY
}

const KnockChatListInvalidator: React.FC = () => {
  const { feedClient } = useKnockFeed()
  const queryClient = useQueryClient()

  const handleRealtimeItemsReceived = useCallback(
    (payload: FeedEventPayload) => {
      if (!payload.items.some(isSharedThreadNotification)) {
        return
      }

      extendAuthConsistencyWindow(DEFAULT_AUTH_CONSISTENCY_BOOTSTRAP_WINDOW_MS)
      const queryKey = chatSessionListQueryKeys.all()
      void queryClient.invalidateQueries({
        queryKey,
        exact: false,
        refetchType: 'active',
      })
    },
    [queryClient],
  )

  useEffect(() => {
    feedClient.on('items.received.realtime', handleRealtimeItemsReceived)
    return () => {
      feedClient.off('items.received.realtime', handleRealtimeItemsReceived)
    }
  }, [feedClient, handleRealtimeItemsReceived])

  return null
}

/**
 * Shared Knock provider that should be mounted once at the app root.
 * This ensures only one Knock client and feed subscription exists,
 * preventing duplicate API calls and 429 rate limit errors.
 */
export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const { user } = useAuthInfo()
  const { data: token } = useNotificationToken()
  const { resolvedTheme } = useTheme()

  const isReady = user !== undefined && user !== null && token !== undefined

  const contextValue = useMemo(() => ({ isReady }), [isReady])

  // Don't render Knock providers until we have authentication
  // This prevents 401 errors from the Knock API
  if (!isReady) {
    return (
      <KnockContext.Provider value={contextValue}>
        {children}
      </KnockContext.Provider>
    )
  }

  return (
    <KnockContext.Provider value={contextValue}>
      <KnockProviderBase
        apiKey={KNOCK_PUBLIC_KEY}
        userId={user.userId}
        userToken={token}
      >
        <KnockFeedProvider
          feedId={KNOCK_IN_APP_CHANNEL_ID}
          colorMode={resolvedTheme === 'dark' ? 'dark' : 'light'}
        >
          <KnockChatListInvalidator />
          {children}
        </KnockFeedProvider>
      </KnockProviderBase>
    </KnockContext.Provider>
  )
}
